import { DeployedSinglePathMission } from '../models/DeployedSinglePathMission';
import { LayerType } from '../models/enums/LayerType';
import { RosMissionStatus } from '../models/enums/RosMissionStatus';
import { FieldOperationsModel } from '../models/FieldOperationsModel';
import { MissionStatusAutoTransitionMessage } from '../models/mqttPayload/MissionStatusAutoTransitionMessage';
import { SimplePath } from '../models/SimplePath';
import { SinglePathMission } from '../models/SinglePathMission';
import { Guid } from 'guid-typescript';
import { defineStore } from 'pinia';

export const useMissionsStore = defineStore('missions', {
  // arrow function recommended for full type inference
  state: () => ({
    mission: null as SinglePathMission | null,
    deployedMission: null as DeployedSinglePathMission | null,
    editedMission: null as SinglePathMission | null,
    selectedMissionIndex: null as number | null,
    fieldAreaM2: 0,
    workAreaAreaM2: 0,
    pathLengthKm: 0,
    missions: [] as Array<SinglePathMission>,
    deployedMissionPause: false,
    editMode: false,
    applicationParameters: new Map() as Map<string, string>,
    newMissionField: null as FieldOperationsModel | null,
    newMissionPath: null as SimplePath | null,
    jobId: Guid.createEmpty().toString(),
    currentMissionStatus: null as MissionStatusAutoTransitionMessage | null,
    currentLayers: [] as LayerType[]
  }),
  getters: {
    renderLayers(): LayerType[] {
      return this.currentLayers;
    },
    remainingDurationSec(): number {
      const remainingSeconds = this.currentMissionStatus?.RosMissionStatus != null ? this.currentMissionStatus.RosMissionStatus.time_remaining_sec : 0;
      return Number(remainingSeconds.toFixed(2));
    },
    remainingDurationHoursMinuteFormat(): string {
      const totalSeconds = this.remainingDurationSec;
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);

      return `${hours} h ${minutes} min`;
    },
    missionPercent(): number {
      return this.currentMissionStatus != null && this.currentMissionStatus.RosMissionStatus != null
        ? this.currentMissionStatus.RosMissionStatus.percent_progress
        : 0;
    },
    missionPercentLabel() {
      const percent: number = this.missionPercent;
      return Math.round(percent * 10) / 10;
    },
    missionDuration(): number {
      if (this.pathLengthKm <= 0 || this.deployedMission == null || this.deployedMission.TargetSpeedKph <= 0) return 0;
      const pathLength = this.pathLengthKm;
      const missionSpeed = this.deployedMission?.TargetSpeedKph as number;
      return pathLength >= 0 && missionSpeed >= 0 ? pathLength / missionSpeed : 0;
    },
    remainingDuration() {
      if (this.pathLengthKm <= 0 || this.deployedMission == null || this.deployedMission.TargetSpeedKph <= 0) return 0;
      const missionPercentComplete: number =
        this.currentMissionStatus?.RosMissionStatus != null ? this.currentMissionStatus.RosMissionStatus.percent_progress : 0;
      const percentRemaining = (100 - missionPercentComplete) / 100;

      const totalMissionDuration: number = this.missionDuration;
      return Number((totalMissionDuration * percentRemaining).toFixed(2));
    },
    selectedMission(): SinglePathMission | null {
      return this.mission;
    },
    missionStatus(): MissionStatusAutoTransitionMessage | null {
      return this.currentMissionStatus;
    },
    missionStatusValid(): boolean {
      if (this.missionStatus == null) return false;
      if (this.missionStatus.AutoTransitionRequirements == null || this.missionStatus.AutoTransitionRequirements.requirements == null) return true;
      let valid = true;
      const collection = this.missionStatus.AutoTransitionRequirements.requirements;

      for (const col of collection) {
        const val: string | boolean = col.value;
        if (!val) valid = false;
      }

      return valid;
    },
    autoRequirementsDisplay(): Array<string> {
      const req: Array<string> = [];
      if (
        this.missionStatus == null ||
        this.missionStatus.AutoTransitionRequirements == null ||
        this.missionStatus.AutoTransitionRequirements.requirements == null
      )
        return req;

      const collection = this.missionStatus.AutoTransitionRequirements.requirements;

      for (const col of collection) {
        const val: string | boolean = col.value;
        if (!val) req.push(col.key);
      }

      return req;
    },
    missionComplete(): boolean {
      return this.missionStatus?.RosMissionStatus?.mission_status == RosMissionStatus.MissionCompleted;
    },
    availableMissions(): Array<SinglePathMission> {
      return this.missions;
    },
    currentMissionIndex(): number | null {
      return this.selectedMissionIndex;
    },
    currentApplicationParameters(): Map<string, string> {
      return this.applicationParameters;
    },
    getEditedMission(): SinglePathMission | null {
      return this.editedMission;
    },
    currentDeployedSinglePathMission(): DeployedSinglePathMission | null {
      return this.deployedMission;
    }
  },
  actions: {
    addLayer(layer: LayerType) {
      if (this.currentLayers.some((o) => o == layer)) return;
      this.currentLayers.push(layer);
    },
    clearLayers() {
      this.currentLayers = [];
    },
    setActiveJob(currentJobId: string) {
      this.jobId = currentJobId;
    },
    setMissionStatus(currentMissionStatus: MissionStatusAutoTransitionMessage | null) {
      this.currentMissionStatus = currentMissionStatus;
    },
    setDeployedMission(deployedMission: DeployedSinglePathMission | null) {
      this.deployedMission = deployedMission;
      if (deployedMission == null) this.deployedMissionPause = false;
    },
    setDeployedMissionPause(state: boolean) {
      this.deployedMissionPause = state;
    },
    selectMission(mission: SinglePathMission | null) {
      this.fieldAreaM2 = 0;
      this.pathLengthKm = 0;
      this.workAreaAreaM2 = 0;
      this.mission = mission;
      if (mission != null) {
        this.applicationParameters = mission.getApplicationParameters();
      }
    },
    setAvailableMissions(missions: Array<SinglePathMission>) {
      this.missions = missions;
    },
    setFieldAreaM2(area: number) {
      this.fieldAreaM2 = area;
    },
    editMission(value: boolean) {
      this.editMode = value;
    },
    setSelectedMissionIndex(index: number | null) {
      this.selectedMissionIndex = index;
    },
    setApplicationParameters(params: Map<string, string>) {
      this.applicationParameters = params;
    },
    setEditedMission(mission: SinglePathMission | null) {
      this.fieldAreaM2 = 0;
      this.pathLengthKm = 0;
      this.workAreaAreaM2 = 0;
      this.editedMission = mission;
    },
    setWorkAreaAreaM2(area: number) {
      this.workAreaAreaM2 = area;
    },
    setNewMissionField(field: FieldOperationsModel | null) {
      this.fieldAreaM2 = 0;
      this.newMissionField = field;
    },
    setNewMissionPath(path: SimplePath | null) {
      this.pathLengthKm = 0;
      this.workAreaAreaM2 = 0;
      this.newMissionPath = path;
    },
    setPathLengthKm(length: number) {
      this.pathLengthKm = length;
    }
  }
});
